'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '@workspace/ui/components/button';
import { Sentinel } from '@workspace/ui/components/custom/sentinel';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { useIntersectionObserver, useIsMobile, useIsTablet } from '@workspace/ui/hooks';
import { cn } from '@workspace/ui/lib';
import { ChevronUp, File, LoaderCircle } from 'lucide-react';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import type { ColumnInfiniteScrollTable } from '@workspace/ui/components/custom/infinite-scroll-table';

export type { ColumnInfiniteScrollTable };

const ITEM_HEIGHT = 72;
const GROUP_HEADER_HEIGHT = 28;
const GROUP_END_PADDING = 24;
const ROW_GAP = 12;

interface GroupedVirtualEntry<TData> {
  key: string;
  item: TData;
  groupLabel: string | null;
  isLastInGroup: boolean;
  height: number;
}

const groupedTableHeaderWrapperClassName =
  'w-full shrink-0 self-stretch rounded-bl-lg rounded-br-lg bg-grey-50 py-1 dark:bg-grey-900';

const groupedTableHeaderCellClassName = 'flex items-center justify-start py-1 pl-2';

const defaultTableHeaderCellClassName =
  'text-grey-500 dark:text-grey-400 line-clamp-1 text-xs font-medium uppercase leading-3';

function getGroupedColumnGridTemplate<TData>(cols: ColumnInfiniteScrollTable<TData>[]) {
  return cols.map((c) => (c.size !== undefined ? `${c.size}fr` : 'minmax(0,1fr)')).join(' ');
}

interface GroupedTableItemProps<TData> {
  item: TData;
  columns: ColumnInfiniteScrollTable<TData>[];
}

function GroupedInfiniteScrollTableItem<TData>({ item, columns }: GroupedTableItemProps<TData>) {
  return (
    <div
      className="grid h-[72px] w-full min-w-0 shrink-0 items-center gap-1 px-2"
      style={{ gridTemplateColumns: getGroupedColumnGridTemplate(columns) }}
    >
      {columns.map((column) => (
        <div key={column.key} className={groupedTableColumnCellClassName}>
          {column.render(item)}
        </div>
      ))}
    </div>
  );
}

interface GroupedInfiniteScrollTableRowProps<TData> {
  entry: GroupedVirtualEntry<TData>;
  columns: ColumnInfiniteScrollTable<TData>[];
  renderGroupLabel: (label: string) => ReactNode;
}

function GroupedInfiniteScrollTableRow<TData>({
  entry,
  columns,
  renderGroupLabel,
}: GroupedInfiniteScrollTableRowProps<TData>) {
  return (
    <div className={cn('flex h-full w-full flex-col', entry.isLastInGroup && 'pb-6')}>
      {entry.groupLabel ? (
        <div className="h-[28px] w-full shrink-0 overflow-hidden">
          {renderGroupLabel(entry.groupLabel)}
        </div>
      ) : null}
      <GroupedInfiniteScrollTableItem item={entry.item} columns={columns} />
    </div>
  );
}

/** Grid cell: min-w-0 so truncated content works inside grid tracks. */
const groupedTableColumnCellClassName = 'flex w-full min-w-0 items-center';

export interface GroupedInfiniteScrollTableProps<TData, TPageData = { data: TData[] }> {
  columns: ColumnInfiniteScrollTable<TData>[];
  mobileColumns: ColumnInfiniteScrollTable<TData>[];
  tabletColumns?: ColumnInfiniteScrollTable<TData>[];
  query: UseInfiniteQueryResult<TPageData>;
  /** Maps loaded pages into flat row data. */
  mapFlatData?: (pages: TPageData[]) => TData[];
  /** Returns a group label when this item starts a new group compared to the previous item. */
  getGroupLabel?: (item: TData, previousItem: TData | null) => string | null;
  /** Stable key for each data item. Defaults to `item.id` when present. */
  getItemKey?: (item: TData) => string | number;
  /** Applies to every header cell unless a column has its own `headerClassName`. */
  tableHeaderCellClassName?: string;
  /** Optional class for the column header row wrapper (e.g. background bar). */
  tableHeaderWrapperClassName?: string;
  /** Optional padding classes for the column header row grid. */
  tableHeaderRowPaddingClassName?: string;
  renderGroupLabel: (label: string) => ReactNode;
  loadingText?: string;
}

export function GroupedInfiniteScrollTable<TData, TPageData = { data: TData[] }>({
  columns,
  mobileColumns,
  tabletColumns,
  query,
  mapFlatData,
  getGroupLabel,
  getItemKey,
  tableHeaderCellClassName,
  tableHeaderWrapperClassName,
  tableHeaderRowPaddingClassName,
  renderGroupLabel,
  loadingText,
}: GroupedInfiniteScrollTableProps<TData, TPageData>) {
  const [showBackToTopButton, setShowBackToTopButton] = useState(false);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const activeColumns = useMemo(() => {
    if (isMobile) {
      return mobileColumns;
    }
    if (isTablet && tabletColumns) {
      return tabletColumns;
    }
    return columns;
  }, [isMobile, isTablet, mobileColumns, tabletColumns, columns]);

  const showColumnHeaders = useMemo(
    () => activeColumns.some(({ header }) => header !== undefined),
    [activeColumns]
  );

  const extractData = useCallback((page: TPageData) => {
    if (typeof page === 'object' && page !== null && 'data' in page) {
      return (page as { data: TData[] }).data;
    }
    return [];
  }, []);

  const resolveItemKey = useCallback(
    (item: TData, index: number) => {
      if (getItemKey) {
        return getItemKey(item);
      }

      if (typeof item === 'object' && item !== null && 'id' in item) {
        return (item as { id: string | number }).id;
      }

      return index;
    },
    [getItemKey]
  );

  const allData = useMemo(() => {
    const infiniteData = query.data as InfiniteData<TPageData> | undefined;
    if (!infiniteData?.pages.length) return [];

    if (mapFlatData) {
      return mapFlatData(infiniteData.pages);
    }

    return infiniteData.pages.flatMap((page) => extractData(page));
  }, [query.data, extractData, mapFlatData]);

  const hasMore = query.hasNextPage ?? false;

  const loading = query.isLoading || query.isFetchingNextPage;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [hasMore, query]);

  const virtualEntries = useMemo((): GroupedVirtualEntry<TData>[] => {
    return allData.map((item, index) => {
      const previousItem = index > 0 ? (allData[index - 1] ?? null) : null;
      const nextItem = index < allData.length - 1 ? (allData[index + 1] ?? null) : null;
      const groupLabel = getGroupLabel?.(item, previousItem) ?? null;
      const isLastInGroup =
        nextItem === null ||
        (getGroupLabel !== undefined && getGroupLabel(nextItem, item) !== null);
      const baseHeight = groupLabel ? GROUP_HEADER_HEIGHT + ITEM_HEIGHT : ITEM_HEIGHT;
      const height = isLastInGroup ? baseHeight + GROUP_END_PADDING : baseHeight;

      return {
        key: String(resolveItemKey(item, index)),
        item,
        groupLabel,
        isLastInGroup,
        height,
      };
    });
  }, [allData, getGroupLabel, resolveItemKey]);

  const getVirtualItemKey = useCallback(
    (index: number) => virtualEntries[index]?.key ?? index,
    [virtualEntries]
  );

  const getVirtualItemSizeEstimate = useCallback(
    (index: number) => virtualEntries[index]?.height ?? ITEM_HEIGHT,
    [virtualEntries]
  );

  const virtualizer = useVirtualizer({
    count: virtualEntries.length,
    getScrollElement: () => scrollContainerRef.current,
    getItemKey: getVirtualItemKey,
    estimateSize: getVirtualItemSizeEstimate,
    gap: ROW_GAP,
    overscan: 10,
  });

  const topSentinelRef = useIntersectionObserver({
    root: scrollContainerRef,
    onIntersect: useCallback(
      (isAtTop: boolean) => {
        setShowBackToTopButton(!(loading && allData.length > 0) && !isAtTop);
      },
      [loading, allData.length]
    ),
  });

  const sentinelRef = useIntersectionObserver({
    root: scrollContainerRef,
    rootMargin: '100px',
    threshold: 0.1,
    enabled: hasMore && !loading,
    onIntersect: useCallback(
      (isIntersecting: boolean) => {
        if (isIntersecting && hasMore && !query.isFetchingNextPage) {
          handleLoadMore();
        }
      },
      [hasMore, query.isFetchingNextPage, handleLoadMore]
    ),
  });

  const scrollToTop = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    scrollContainerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  return (
    <div
      className={cn('flex h-[688px] w-full flex-col')}
      role="region"
      aria-label="Scrollable data table"
    >
      {showColumnHeaders && allData.length > 0 ? (
        <div
          className={cn(
            'grid w-full min-w-0 shrink-0 items-center',
            groupedTableHeaderWrapperClassName,
            tableHeaderWrapperClassName
          )}
          style={{ gridTemplateColumns: getGroupedColumnGridTemplate(activeColumns) }}
        >
          {activeColumns.map((column) => (
            <div
              key={`header-${column.key}`}
              className={cn(groupedTableHeaderCellClassName, tableHeaderRowPaddingClassName)}
              aria-hidden={column.header == null || column.header === ''}
            >
              <div className="flex flex-1 items-center justify-start gap-1">
                {column.header != null && column.header !== '' ? (
                  <p
                    className={cn(
                      defaultTableHeaderCellClassName,
                      tableHeaderCellClassName,
                      column.headerClassName
                    )}
                  >
                    {column.header}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="h-full overflow-y-auto pr-[18px]"
          role="feed"
          aria-busy={loading}
        >
          <div
            className="relative"
            style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%' }}
          >
            <Sentinel ref={topSentinelRef} className="absolute top-0 left-0" />
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const entry = virtualEntries[virtualItem.index];
              if (!entry) return null;

              return (
                <div
                  key={entry.key}
                  data-index={virtualItem.index}
                  className="absolute top-0 left-0 w-full overflow-hidden"
                  style={{
                    height: entry.height,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <GroupedInfiniteScrollTableRow
                    entry={entry}
                    columns={activeColumns}
                    renderGroupLabel={renderGroupLabel}
                  />
                </div>
              );
            })}

            {hasMore && (
              <Sentinel
                ref={sentinelRef}
                className="absolute top-0 left-0"
                style={{ top: `${virtualizer.getTotalSize()}px` }}
              />
            )}
          </div>
        </div>

        {showBackToTopButton && (
          <Button
            onClick={scrollToTop}
            variant="primary"
            size="xs"
            className={cn('absolute bottom-4 left-1/2 z-10 -translate-x-1/2')}
          >
            <ChevronUp className="size-4" />
            Back to top
          </Button>
        )}
      </div>

      {loading && allData.length > 0 && (
        <div className="flex items-center justify-center gap-2 py-8">
          <LoaderCircle className="size-3 animate-spin text-neutral-200 dark:text-neutral-100" />
          <div className="text-xs font-medium text-neutral-200 dark:text-neutral-100">
            {loadingText ?? 'Loading more'}
          </div>
        </div>
      )}

      {!loading && allData.length === 0 && (
        <div className={cn('flex h-full w-full flex-col items-center justify-center gap-2')}>
          <File className="text-grey-400 h-20 w-20 p-4" />
          <div className="flex flex-col items-center justify-center gap-2 text-center">
            <span className="text-lg leading-normal font-medium text-neutral-200 dark:text-neutral-100">
              No results found
            </span>
            <span className="text-grey-700 dark:text-grey-400 max-w-[265px] text-center text-sm leading-4 text-wrap">
              Sorry, we couldn&apos;t find any results matching your filters.
            </span>
          </div>
        </div>
      )}

      {loading && allData.length === 0 && (
        <div className="flex flex-col gap-3 pr-[18px]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="grid h-[72px] w-full min-w-0 items-center gap-1 px-2"
              style={{ gridTemplateColumns: getGroupedColumnGridTemplate(activeColumns) }}
            >
              {activeColumns.map((column) => (
                <div key={column.key} className={groupedTableColumnCellClassName}>
                  <Skeleton className="bg-grey-200 dark:bg-grey-800 h-6 w-[80%]" />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
